import { Box, Button, ButtonGroup, Input, Typography } from '@mui/material';
// Components
import { LoadingSpinner } from '@/common/index';
// Hooks
import { useAuth } from '@/hooks/useAuth';
import { useCartContext } from '@/providers/CartProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
// Styles
import styles from './quantityButtons.module.css';

const QuantityButtons = ({
  quantity,
  setQuantity,
  buttonText,
  productId,
  existingQuantity = 0,
  isCartItem = false,
  disabled,
  quantityLow,
  productQuantity,
}) => {
  const { status } = useAuth();
  const {
    addItemToCart,
    cartLoading,
    clearCartError,
    createPendingTransaction,
    removeItemFromCart,
  } = useCartContext();
  const isMobile = useIsMobile(520);

  const increment = () => {
    resetErrors();
    if (quantityLow && quantity >= productQuantity) {
      setQuantity(productQuantity);
    } else {
      setQuantity(quantity + 1);
    }
  };

  const decrement = () => {
    resetErrors();
    quantity > 0 && setQuantity(quantity - 1);
  };

  const handleSetQuantity = (e) => {
    const { value } = e.target;
    // Don't allow purchase higher than remaining quantity when limited
    if (quantityLow && value > productQuantity) {
      setQuantity(productQuantity);
      return;
    }
    // Set to 0 if no value (ie. on delete)
    if (!value) {
      setQuantity(0);
      return;
    }
    // Do nothing if value isNaN
    if (isNaN(+value)) {
      return;
    }
    resetErrors();
    setQuantity(+value);
  };

  const resetErrors = () => {
    clearCartError();
  };

  const handleUpdateQuantity = async () => {
    if (status !== 'signedIn') return createPendingTransaction(productId, quantity);
    if (!existingQuantity)
      return await addItemToCart({ productId, quantity, showModal: true });
    // Removes the difference between existingQuantity & quantity
    if (existingQuantity >= quantity) {
      const removeQty = existingQuantity - quantity;
      return await removeItemFromCart({ productId, quantity: removeQty });
      // Adds the difference between quantity & existingQuantity
    } else {
      const addQty = quantity - existingQuantity;
      return await addItemToCart({ productId, quantity: addQty });
    }
  };

  const isAddUpdateDisabled = isCartItem ? existingQuantity === quantity : !quantity;

  return (
    <>
      <div className={styles.productQuantity}>
        {!isCartItem ? (
          <Box>
            <Typography variant="h5" data-core-qa="singleProductQty">
              Quantity
            </Typography>
          </Box>
        ) : null}

        <div className={styles.buttonsWrapper}>
          <ButtonGroup variant="store" size="small" color="grey" disableRipple>
            <Button
              variant="store"
              onClick={decrement}
              disabled={!quantity || disabled}
              data-core-qa="decrementQuantityButtonText"
            >
              &#8722;
            </Button>
            <Input
              className="quantityField"
              inputProps={{
                min: 0,
                style: {
                  textAlign: 'center',
                  width: '50px',
                  cursor: 'pointer',
                },
              }}
              color="grey"
              min="1"
              value={quantity}
              inputMode="numeric"
              onChange={handleSetQuantity}
              onFocus={resetErrors}
              onBlur={resetErrors}
              disabled={cartLoading || disabled}
              data-core-qa="quantityInputText"
            />
            <Button
              variant="store"
              disableFocusRipple
              onClick={increment}
              data-core-qa="incrementQuantityButtonText"
              disabled={disabled || (quantityLow && quantity >= productQuantity)}
            >
              &#43;
            </Button>
          </ButtonGroup>
        </div>
      </div>
      <div className={styles.buttonRow}>
        <Button
          variant="contained"
          disabled={isAddUpdateDisabled || disabled}
          onClick={handleUpdateQuantity}
          data-core-qa="addUpdateButtonText"
          size="large"
          fullWidth={isMobile}
          sx={{ minWidth: '140px' }}
        >
          {cartLoading ? <LoadingSpinner /> : buttonText}
        </Button>
      </div>
    </>
  );
};

export default QuantityButtons;
